@file:Suppress("detekt:TooGenericExceptionCaught")

package io.peekandpoke.kraft.semanticui.forms

import io.peekandpoke.kraft.components.Ctx
import io.peekandpoke.kraft.components.comp
import io.peekandpoke.kraft.forms.AbstractFormField
import io.peekandpoke.kraft.forms.FieldOptions
import io.peekandpoke.kraft.forms.SettingsBuilder
import io.peekandpoke.kraft.i18n.generated.forms
import io.peekandpoke.kraft.i18n.generated.invalidValue
import io.peekandpoke.kraft.semanticui.forms.UiInputFieldComponent.Options
import io.peekandpoke.kraft.utils.dateToYmd
import io.peekandpoke.kraft.utils.localDateTimeToYmdHms
import io.peekandpoke.kraft.utils.numberToString
import io.peekandpoke.kraft.utils.stringToDate
import io.peekandpoke.kraft.utils.stringToDateOrNull
import io.peekandpoke.kraft.utils.stringToDouble
import io.peekandpoke.kraft.utils.stringToFloat
import io.peekandpoke.kraft.utils.stringToInt
import io.peekandpoke.kraft.utils.stringToLocalDateTime
import io.peekandpoke.kraft.utils.stringToLocalDateTimeOrNull
import io.peekandpoke.kraft.utils.stringToLocalTime
import io.peekandpoke.kraft.utils.stringToLocalTimeOrNull
import io.peekandpoke.kraft.utils.stringToString
import io.peekandpoke.kraft.utils.stringToZonedDateTime
import io.peekandpoke.kraft.utils.stringToZonedDateTimeOrNull
import io.peekandpoke.kraft.utils.timeToHms
import io.peekandpoke.kraft.utils.zonedDateTimeToYmdHms
import io.peekandpoke.kraft.vdom.VDom
import io.peekandpoke.ultra.datetime.MpInstant
import io.peekandpoke.ultra.datetime.MpLocalDate
import io.peekandpoke.ultra.datetime.MpLocalDateTime
import io.peekandpoke.ultra.datetime.MpLocalTime
import io.peekandpoke.ultra.datetime.MpTimezone
import io.peekandpoke.ultra.datetime.MpZonedDateTime
import io.peekandpoke.ultra.html.key
import io.peekandpoke.ultra.html.onChange
import io.peekandpoke.ultra.html.onInput
import io.peekandpoke.ultra.semanticui.ui
import kotlinx.browser.document
import kotlinx.html.FlowContent
import kotlinx.html.INPUT
import kotlinx.html.InputType
import kotlinx.html.Tag
import kotlinx.html.input
import org.w3c.dom.HTMLInputElement
import kotlin.reflect.KMutableProperty0

/** Provides a [UiInputFieldRenderer] for rendering text and number input fields. */
val Tag.UiInputField get() = UiInputFieldRenderer(this)

/** Provides a [UiPasswordFieldRenderer] for rendering password input fields. */
val Tag.UiPasswordField get() = UiPasswordFieldRenderer(this)

/** Provides a [UiDateFieldRenderer] for rendering date input fields. */
val Tag.UiDateField get() = UiDateFieldRenderer(this)

/** Provides a [UiDateTimeFieldRenderer] for rendering date-time input fields. */
val Tag.UiDateTimeField get() = UiDateTimeFieldRenderer(this)

/** Provides a [UiTimeFieldRenderer] for rendering time input fields. */
val Tag.UiTimeField get() = UiTimeFieldRenderer(this)

/**
 * Generic input field factory that renders a [UiInputFieldComponent] with custom serialization.
 *
 * @param value The current value.
 * @param onChange Callback invoked when the value changes.
 * @param toStr Converts [T] to the string displayed in the input.
 * @param fromStr Parses user input back to [T].
 * @param builder Optional configuration for field options.
 */
@Suppress("FunctionName")
fun <T> Tag.UiInputField(
    value: T,
    onChange: (T) -> Unit,
    toStr: (T) -> String,
    fromStr: (String) -> T,
    builder: Options<T>.() -> Unit = {},
) = comp(
    UiInputFieldComponent.Props(
        value = value,
        onChange = onChange,
        toStr = toStr,
        fromStr = fromStr,
        options = Options<T>().apply(builder),
    )
) {
    UiInputFieldComponent(it)
}

/**
 * Semantic UI input field component with validation, formatting, and type support.
 *
 * Wraps an HTML `<input>` element and integrates with the Kraft forms system.
 */
class UiInputFieldComponent<T, P : UiInputFieldComponent.Props<T>>(ctx: Ctx<P>) :
    AbstractFormField<T, Options<T>, P>(ctx) {

    /** Configuration options for [UiInputFieldComponent], combining field and Semantic UI options. */
    class Options<T> : FieldOptions.Base<T>(), SemanticOptions<T>, SemanticOptions.Input<T>

    /** Props for [UiInputFieldComponent]. */
    data class Props<X>(
        override val value: X,
        override val onChange: (X) -> Unit,
        override val options: Options<X>,
        val toStr: (X) -> String,
        val fromStr: (String) -> X,
    ) : AbstractFormField.Props<X, Options<X>>

    /** The underlying HTML input element. */
    val inputElement: HTMLInputElement get() = dom!!.querySelector("input") as HTMLInputElement

    private var userInput: String by value(props.toStr(props.value))

    internal var typeOverride: InputType? by value(null)

    internal val effectiveType: InputType? get() = typeOverride ?: options.type()

    init {
        lifecycle {
            onMount {
                props.options.autofocusValue()?.takeIf { it }?.let {
                    focus()
                }
            }

            onNextProps { new, old ->
                if (new.value != old.value) {
                    // We keep the current [userInput] as long when it fromStr value is the same as the current value
                    val userMapped = try {
                        props.fromStr(userInput)
                    } catch (_: Throwable) {
                        null
                    }

                    if (userMapped != currentValue) {
                        userInput = new.toStr(new.value)
                    }
                }
            }
        }
    }

    /**
     * Sets the focus on the input element
     */
    fun focus() {
        inputElement.focus()
    }

    /**
     * Returns true when the text area has the focus.
     */
    fun hasFocus(): Boolean {
        return document.activeElement === inputElement
    }

    override fun VDom.render() {
        ui.with(options.appear.getOrDefault { this }).given(hasErrors) { error }.field {
            key = options.domKey.getOrDefault(autoDomKey)

            renderLabel("input")

            val wrap = options.wrapFieldWith()

            when {
                wrap != null -> ui.wrap().then {
                    options.renderBeforeField()?.let { it(this, this@UiInputFieldComponent) }

                    renderField()

                    options.renderAfterField()?.let { it(this, this@UiInputFieldComponent) }
                }

                else -> renderField()
            }

            renderErrors(this)
        }
    }

    /** Parses and applies the raw [input] string, updating the field value or showing validation errors. */
    fun setInput(input: String) {
        try {
            userInput = input
            val newValue = props.fromStr(input)
            setValue(newValue)
        } catch (t: Throwable) {
            console.warn(t)
            errors = listOf(translate.forms.invalidValue())
        }
    }

    // Private helpers ////////////////////////////////////////////////////////////////////////////////////

    private fun FlowContent.renderField() {
        input {
            applyAll()
        }
    }

    private fun valueAsString(): String {
        return userInput
    }

    fun INPUT.applyAll() {
        setValue()
        track()

        applyDisabled()
        applyFormatValue()
        applyName()
        applyPlaceholder()
        applyStep()
        applyType()
        applyRequired()
    }

    private fun INPUT.setValue() {
        value = valueAsString()
    }

    private fun INPUT.track() {
        onInput {
            val elem = (it.target as HTMLInputElement)
            setInput(elem.value)
        }

        onChange {
            val elem = (it.target as HTMLInputElement)
            setInput(elem.value)
        }
    }

    private fun INPUT.applyDisabled() {
        if (options.isDisabled) {
            disabled = true
        }
    }

    private fun INPUT.applyFormatValue() {
        options.formatValue()?.let { attributes["format-value"] = it }
    }

    private fun INPUT.applyPlaceholder() {
        options.placeholder()?.takeIf { it.isNotBlank() }?.let { placeholder = it }
    }

    private fun INPUT.applyName() {
        options.name()?.let { name = it }
    }

    private fun INPUT.applyStep() {
        options.step()?.let { step = it.toString() }
    }

    private fun INPUT.applyType() {
        effectiveType?.let { type = it }
    }

    private fun INPUT.applyRequired() {
        options.required()?.let { required = it }
    }
}

/**
 * DSL renderer for [UiInputFieldComponent], providing overloads for String, Int, Float, and Double types.
 *
 * Accessed via [Tag.UiInputField].
 */
class UiInputFieldRenderer(private val tag: Tag) {
    /**
     * Renders the field for a String
     */
    operator fun invoke(
        prop: KMutableProperty0<String>,
        builder: Options<String>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for a String
     */
    operator fun invoke(
        value: String,
        onChange: (String) -> Unit,
        builder: Options<String>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::stringToString, ::stringToString, builder)

    /**
     * Renders the field for a nullable String
     */
    fun nullable(
        prop: KMutableProperty0<String?>,
        builder: Options<String?>.() -> Unit = {},
    ) = nullable(prop(), prop::set, builder)

    /**
     * Renders the field for a nullable String
     */
    fun nullable(
        value: String?,
        onChange: (String?) -> Unit,
        builder: Options<String?>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::stringToString, ::stringToString, builder)

    /**
     * Renders the field for an Int
     */
    operator fun invoke(
        prop: KMutableProperty0<Int>,
        builder: Options<Int>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for an Int
     */
    operator fun invoke(
        value: Int,
        onChange: (Int) -> Unit,
        builder: Options<Int>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::numberToString, ::stringToInt) {
        type(InputType.number)
        builder()
    }

    /**
     * Renders the field for a nullable Int
     */
    fun nullable(
        prop: KMutableProperty0<Int?>,
        builder: Options<Int?>.() -> Unit = {},
    ) = nullable(prop(), prop::set, builder)

    /**
     * Renders the field for a nullable Int
     */
    fun nullable(
        value: Int?,
        onChange: (Int?) -> Unit,
        builder: Options<Int?>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::numberToString, ::stringToInt) {
        type(InputType.number)
        builder()
    }

    /**
     * Renders the field for a Float
     */
    operator fun invoke(
        prop: KMutableProperty0<Float>,
        builder: Options<Float>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for a Float
     */
    operator fun invoke(
        value: Float,
        onChange: (Float) -> Unit,
        builder: Options<Float>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::numberToString, ::stringToFloat) {
        type(InputType.number)
        builder()
    }

    /**
     * Renders the field for a nullable Float
     */
    fun nullable(
        prop: KMutableProperty0<Float?>,
        builder: Options<Float?>.() -> Unit = {},
    ) = nullable(prop(), prop::set, builder)

    /**
     * Renders the field for a nullable Float
     */
    fun nullable(
        value: Float?,
        onChange: (Float?) -> Unit,
        builder: Options<Float?>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::numberToString, ::stringToFloat) {
        type(InputType.number)
        builder()
    }

    /**
     * Renders the field for a Double
     */
    operator fun invoke(
        prop: KMutableProperty0<Double>,
        builder: Options<Double>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for a Double
     */
    operator fun invoke(
        value: Double,
        onChange: (Double) -> Unit,
        builder: Options<Double>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::numberToString, ::stringToDouble) {
        type(InputType.number)
        builder()
    }

    /**
     * Renders the field for a nullable Double
     */
    fun nullable(
        prop: KMutableProperty0<Double?>,
        builder: Options<Double?>.() -> Unit = {},
    ) = nullable(prop(), prop::set, builder)

    /**
     * Renders the field for a nullable Double
     */
    fun nullable(
        value: Double?,
        onChange: (Double?) -> Unit,
        builder: Options<Double?>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::numberToString, ::stringToDouble) {
        type(InputType.number)
        builder()
    }
}

/** DSL renderer for password input fields. Accessed via [Tag.UiPasswordField]. */
class UiPasswordFieldRenderer(private val tag: Tag) {
    /**
     * Renders the field for a String
     */
    operator fun invoke(
        prop: KMutableProperty0<String>,
        builder: Options<String>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for a String
     */
    operator fun invoke(
        value: String,
        onChange: (String) -> Unit,
        builder: Options<String>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::stringToString, ::stringToString) {
        type(InputType.password)
        builder()
    }
}

/**
 * DSL renderer for date input fields, supporting MpLocalDate, MpLocalDateTime, and MpZonedDateTime.
 *
 * Accessed via [Tag.UiDateField].
 */
class UiDateFieldRenderer(private val tag: Tag) {
    /**
     * Renders the field for an MpLocalDate
     */
    operator fun invoke(
        prop: KMutableProperty0<MpLocalDate>,
        builder: Options<MpLocalDate>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for an MpLocalDate
     */
    operator fun invoke(
        value: MpLocalDate,
        onChange: (MpLocalDate) -> Unit,
        builder: Options<MpLocalDate>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::dateToYmd, ::stringToDate) {
        asDateInput()
        required(true)
        builder()
    }

    /**
     * Renders the field for a nullable MpLocalDate
     */
    fun nullable(
        prop: KMutableProperty0<MpLocalDate?>,
        builder: Options<MpLocalDate?>.() -> Unit = {},
    ) = nullable(prop(), prop::set, builder)

    /**
     * Renders the field for a nullable MpLocalDate
     */
    fun nullable(
        value: MpLocalDate?,
        onChange: (MpLocalDate?) -> Unit,
        builder: Options<MpLocalDate?>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::dateToYmd, ::stringToDateOrNull) {
        asDateInput()
        builder()
    }

    /**
     * Renders the field for an MpLocalDateTime
     */
    operator fun invoke(
        prop: KMutableProperty0<MpLocalDateTime>,
        builder: SettingsBuilder<MpLocalDate> = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for an MpLocalDateTime
     */
    operator fun invoke(
        value: MpLocalDateTime,
        onChange: (MpLocalDateTime) -> Unit,
        builder: io.peekandpoke.kraft.forms.SettingsBuilder<MpLocalDate> = {},
    ) = invoke(
        value = value.toDate(),
        onChange = { onChange(it.atTime(value.toTime())) },
        builder = builder
    )

    /**
     * Renders the field for a nullable MpLocalDateTime
     */
    fun nullable(
        prop: KMutableProperty0<MpLocalDateTime?>,
        builder: io.peekandpoke.kraft.forms.SettingsBuilder<MpLocalDate?> = {},
    ) = nullable(prop(), prop::set, builder)

    /**
     * Renders the field for a nullable MpLocalDateTime
     */
    fun nullable(
        value: MpLocalDateTime?,
        onChange: (MpLocalDateTime?) -> Unit,
        builder: Options<MpLocalDate?>.() -> Unit = {},
    ) = nullable(
        value = value?.toDate(),
        onChange = {
            onChange(
                it?.atTime(value?.toTime() ?: MpLocalTime.Min)
            )
        },
        builder = builder
    )

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        prop: KMutableProperty0<MpZonedDateTime>,
        builder: Options<MpLocalDate>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        value: MpZonedDateTime,
        onChange: (MpZonedDateTime) -> Unit,
        builder: Options<MpLocalDate>.() -> Unit = {},
    ) = invoke(
        value = value,
        timezone = value.timezone,
        onChange = onChange,
        builder = builder
    )

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        prop: KMutableProperty0<MpZonedDateTime>,
        timezone: MpTimezone,
        builder: Options<MpLocalDate>.() -> Unit = {},
    ) = invoke(prop(), timezone, prop::set, builder)

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        value: MpZonedDateTime,
        timezone: MpTimezone,
        onChange: (MpZonedDateTime) -> Unit,
        builder: Options<MpLocalDate>.() -> Unit = {},
    ) = invoke(
        value = value.toLocalDate(),
        onChange = { onChange(it.atStartOfDay(timezone).atTime(value.toLocalTime())) },
        builder = builder
    )

    /**
     * Renders the field for a nullable MpZonedDateTime
     */
    fun nullable(
        prop: KMutableProperty0<MpZonedDateTime?>,
        timezone: MpTimezone,
        builder: Options<MpLocalDate?>.() -> Unit = {},
    ) = nullable(prop(), timezone, prop::set, builder)

    /**
     * Renders the field for a nullable MpZonedDateTime
     */
    fun nullable(
        value: MpZonedDateTime?,
        timezone: MpTimezone,
        onChange: (MpZonedDateTime?) -> Unit,
        builder: Options<MpLocalDate?>.() -> Unit = {},
    ) = nullable(
        value = value?.toLocalDate(),
        onChange = {
            onChange(
                it?.atStartOfDay(timezone)?.atTime(value?.toLocalTime() ?: MpLocalTime.Min)
            )
        },
        builder = builder
    )
}

/**
 * DSL renderer for date-time input fields, supporting MpLocalDateTime, MpInstant, and MpZonedDateTime.
 *
 * Accessed via [Tag.UiDateTimeField].
 */
class UiDateTimeFieldRenderer(private val tag: Tag) {

    // MpLocalDateTime /////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Renders the field for an MpLocalDateTime
     */
    operator fun invoke(
        prop: KMutableProperty0<MpLocalDateTime>,
        builder: Options<MpLocalDateTime>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for an MpLocalDateTime
     */
    operator fun invoke(
        value: MpLocalDateTime,
        onChange: (MpLocalDateTime) -> Unit,
        builder: Options<MpLocalDateTime>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::localDateTimeToYmdHms, ::stringToLocalDateTime) {
        asDateTimeInput()
        required(true)
        builder()
    }

    /**
     * Renders the field for a nullable MpLocalDateTime
     */
    fun nullable(
        prop: KMutableProperty0<MpLocalDateTime?>,
        builder: Options<MpLocalDateTime?>.() -> Unit = {},
    ) = nullable(prop(), prop::set, builder)

    /**
     * Renders the field for a nullable MpLocalDateTime
     */
    fun nullable(
        value: MpLocalDateTime?,
        onChange: (MpLocalDateTime?) -> Unit,
        builder: Options<MpLocalDateTime?>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::localDateTimeToYmdHms, ::stringToLocalDateTimeOrNull) {
        asDateTimeInput()
        builder()
    }

    // MpInstant ///////////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Renders the field for an [MpInstant]
     */
    operator fun invoke(
        prop: KMutableProperty0<MpInstant>,
        timezone: MpTimezone,
        builder: Options<MpZonedDateTime>.() -> Unit = {},
    ) = invoke(prop(), timezone, prop::set, builder)

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        value: MpInstant,
        timezone: MpTimezone,
        onChange: (MpInstant) -> Unit,
        builder: Options<MpZonedDateTime>.() -> Unit = {},
    ) = tag.UiInputField(
        value.atZone(timezone),
        { onChange(it.toInstant()) },
        ::zonedDateTimeToYmdHms,
        { stringToZonedDateTime(it).copy(timezone = timezone) },
    ) {
        asDateTimeInput()
        builder()
    }

    /**
     * Renders the field for a nullable MpZonedDateTime
     */
    fun nullable(
        prop: KMutableProperty0<MpInstant?>,
        timezone: MpTimezone,
        builder: Options<MpZonedDateTime?>.() -> Unit = {},
    ) = nullable(prop(), timezone, prop::set, builder)

    /**
     * Renders the field for a nullable MpZonedDateTime
     */
    fun nullable(
        value: MpInstant?,
        timezone: MpTimezone,
        onChange: (MpInstant?) -> Unit,
        builder: Options<MpZonedDateTime?>.() -> Unit = {},
    ) = tag.UiInputField(
        value?.atZone(timezone),
        { onChange(it?.toInstant()) },
        ::zonedDateTimeToYmdHms,
        { stringToZonedDateTimeOrNull(it)?.copy(timezone = timezone) },
    ) {
        asDateTimeInput()
        builder()
    }

    // MpZonedDateTime /////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        prop: KMutableProperty0<MpZonedDateTime>,
        builder: Options<MpZonedDateTime>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        value: MpZonedDateTime,
        onChange: (MpZonedDateTime) -> Unit,
        builder: Options<MpZonedDateTime>.() -> Unit = {},
    ) = invoke(value, value.timezone, onChange, builder)

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        prop: KMutableProperty0<MpZonedDateTime>,
        timezone: MpTimezone,
        builder: Options<MpZonedDateTime>.() -> Unit = {},
    ) = invoke(prop(), timezone, prop::set, builder)

    /**
     * Renders the field for an MpZonedDateTime
     */
    operator fun invoke(
        value: MpZonedDateTime,
        timezone: MpTimezone,
        onChange: (MpZonedDateTime) -> Unit,
        builder: Options<MpZonedDateTime>.() -> Unit = {},
    ) = tag.UiInputField(
        value,
        onChange,
        ::zonedDateTimeToYmdHms,
        { stringToZonedDateTime(it).copy(timezone = timezone) },
    ) {
        asDateTimeInput()
        builder()
    }

    /**
     * Renders the field for a nullable MpZonedDateTime
     */
    fun nullable(
        prop: KMutableProperty0<MpZonedDateTime?>,
        timezone: MpTimezone,
        builder: Options<MpZonedDateTime?>.() -> Unit = {},
    ) = nullable(prop(), timezone, prop::set, builder)

    /**
     * Renders the field for a nullable MpZonedDateTime
     */
    fun nullable(
        value: MpZonedDateTime?,
        timezone: MpTimezone,
        onChange: (MpZonedDateTime?) -> Unit,
        builder: Options<MpZonedDateTime?>.() -> Unit = {},
    ) = tag.UiInputField(
        value,
        onChange,
        ::zonedDateTimeToYmdHms,
        { stringToZonedDateTimeOrNull(it)?.copy(timezone = timezone) },
    ) {
        asDateTimeInput()
        builder()
    }
}

/** DSL renderer for time input fields. Accessed via [Tag.UiTimeField]. */
class UiTimeFieldRenderer(private val tag: Tag) {
    /**
     * Renders the field for an MpLocalTime
     */
    operator fun invoke(
        prop: KMutableProperty0<MpLocalTime>,
        builder: Options<MpLocalTime>.() -> Unit = {},
    ) = invoke(prop(), prop::set, builder)

    /**
     * Renders the field for an MpLocalTime
     */
    operator fun invoke(
        value: MpLocalTime,
        onChange: (MpLocalTime) -> Unit,
        builder: Options<MpLocalTime>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::timeToHms, ::stringToLocalTime) {
        asTimeInput()
        builder()
    }

    /**
     * Renders the field for a nullable MpLocalTime
     */
    operator fun invoke(
        prop: KMutableProperty0<MpLocalTime?>,
        builder: Options<MpLocalTime?>.() -> Unit = {},
    ) = nullable(prop(), prop::set, builder)

    /**
     * Renders the field for a nullable MpLocalTime
     */
    fun nullable(
        value: MpLocalTime?,
        onChange: (MpLocalTime?) -> Unit,
        builder: Options<MpLocalTime?>.() -> Unit = {},
    ) = tag.UiInputField(value, onChange, ::timeToHms, ::stringToLocalTimeOrNull) {
        asTimeInput()
        builder()
    }
}
